import {
  isRangeDecimalVFrame,
  isRangeIntegerVFrame,
  isRangeMoneyDecimalVFrame,
  type RangeFractionVFrame,
  type VFrameBase,
} from '@festina/life-general-types/life'
import { notEmpty, type Optional } from '@festina-life/core'
import {
  FormikNumberInput,
  type FormikNumberInputProps,
} from '@festina-life/ui/formikForms'
import get from 'lodash-es/get'
import { useMemo } from 'react'

import { useAmountFormatter } from '../../../hooks/useAmountFormatter'
import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isDontUseVFrame,
  isFixedVFrame,
} from '../../forms/yup/validation/types'
import { isVFrameWithDefaultValue } from '../../forms/yup/validation/yupValidationUtils'
import { useFieldVFrame } from '../useFieldVFrame'
import type { InputPropsFromFormik } from './types'

type Props = InputPropsFromFormik<FormikNumberInputProps>

type ValidationParams = {
  min: Optional<number>
  max: Optional<number>
  formatter: (value: Optional<string | number>) => string
}

/**
 *   Number input used in life forms. This should be used in those cases where a frame exists to validate the input values.
 */
export const LifeNumberInput = ({ name, disabled, ...props }: Props) => {
  const { values, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(name)
  const { format, formatUnitless } = useAmountFormatter()

  const toValidationParams = useMemo(
    () =>
      (frameStatus: Optional<VFrameBase>): Optional<ValidationParams> => {
        if (!frameStatus) {
          return undefined
        }

        if (
          isRangeDecimalVFrame(frameStatus) ||
          isRangeIntegerVFrame(frameStatus)
        ) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: formatUnitless,
          }
        }

        if (isRangeMoneyDecimalVFrame(frameStatus)) {
          return {
            min: frameStatus.min,
            max: frameStatus.max,
            formatter: format,
          }
        }

        return undefined
      },
    [formatUnitless, format],
  )

  const formatValue = useMemo(
    () => (validationParams: Optional<ValidationParams>) => {
      if (!validationParams) {
        return undefined
      }

      const { min, max, formatter } = validationParams

      return notEmpty(min) || notEmpty(max)
        ? `${notEmpty(min) ? formatter(min) : ''}–${notEmpty(max) ? formatter(max) : ''}`
        : undefined
    },
    [],
  )

  if (isDontUseVFrame(frameStatus)) {
    return null
  }

  const fixed = isFixedVFrame(frameStatus)
  const isRequired = isFieldRequired(frameStatus)

  const value = get(values, name as string)

  const getValue = () => {
    if (fixed) {
      return frameStatus.value
    } else {
      return value
    }
  }

  const defaultValue = isVFrameWithDefaultValue(frameStatus)
    ? (frameStatus as RangeFractionVFrame)?.defaultValue
    : null

  return (
    <FormikNumberInput
      name={name}
      disabled={fixed || disabled}
      defaultValue={defaultValue ?? undefined}
      validationText={formatValue(toValidationParams(frameStatus))}
      onDefaultValueClick={() => setFieldValue(name, defaultValue)}
      isRequired={isRequired}
      hasRightColumnAlways
      {...props}
      value={getValue()}
    />
  )
}
